import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Users, Clock, Layers, Globe2 } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyCHIStLukes: React.FC = () => {
  const navigationStore = useNavigationStore()
  const handleBookDemo = () => navigationStore.openDemoModal()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How CHI St. Luke’s Streamlined International Patient Access with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">A unified referral and eConsult program coordinating care for patients from 85+ countries with 24/7/365 support.</p>
              <div className="mt-8 flex gap-3">
                <button 
                  onClick={() => setDownloadFormOpen(true)} 
                  className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition"
                >
                  Download Full Case Study
                </button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">econsult</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">referral</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Configured for international services: second opinions, eConsults, and referrals with language & logistics support.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Globe2 className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">85+ countries</div>
              <div className="text-slate-600">Global patient reach</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Clock className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">24/7/365</div>
              <div className="text-slate-600">International reps on call</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Layers className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">End-to-end</div>
              <div className="text-slate-600">From eConsult to admission</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Multilingual</div>
              <div className="text-slate-600">Language & cultural services</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">International second opinions and referrals ran through manual channels, complicating intake, eligibility, and cross-border logistics.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Fragmented intake for clinical history, imaging, and documents</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Limited visibility into case status for patients, physicians, and reps</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Complex coordination: visas, travel, lodging, and interpretation</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Manual referrals</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Document collection</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Eligibility & payor checks</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Travel & lodging</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Language support</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">eConsultCX and ReferralCX were configured to centralize intake and triage, route cases to appropriate specialists, and manage end-to-end logistics.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Digital intake & eConsults</div><div className="mt-1 text-slate-600">Patients and physicians submit securely; specialists review asynchronously.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Intelligent routing</div><div className="mt-1 text-slate-600">Rules-based triage to service lines; transparent case status.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Logistics orchestration</div><div className="mt-1 text-slate-600">Visa letters, travel & lodging, interpreters, spiritual & cultural support.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Communication & consent</div><div className="mt-1 text-slate-600">Multilingual messaging and document exchange with audit trails.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">24/7/365 coverage</div><div className="mt-1 text-slate-600">International reps manage handoffs across time zones.</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Faster access</strong> to second opinions and specialty care across borders.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Revenue growth</strong> from increased international referrals and services.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Improved experience</strong> with multilingual communications and coordinated logistics.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">International patients and families receive culturally sensitive support from inquiry to follow-up, with clear status and fewer surprises.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Language assistance, spiritual care, and cultural navigation</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Coordinated travel and lodging near the hospital</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“Centralizing eConsults and referrals made complex cross-border care predictable for our teams and patients.”</blockquote>
            <div className="mt-3 text-slate-600">— International Services Leadership, CHI St. Luke’s</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">A single platform for eConsults, referrals, and logistics enabled a dependable pathway for international access and continuity.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Global readiness</div><div className="text-slate-600 mt-1">Coverage across time zones, cultures, and languages</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Transparent coordination</div><div className="text-slate-600 mt-1">Track case status end-to-end</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Better utilization</div><div className="text-slate-600 mt-1">Right patient, right service, first time</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to transform patient access and care coordination?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can streamline complex workflows across your network.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="CHI St. Luke's International Patient Access Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Case+Study_Baylor.pdf"
      />
    </main>
  )
}

export default CaseStudyCHIStLukes


